/**
 * V2 Login — dark "standard" public /login page (B-258).
 *
 * Google sign-in (PRP-MCP-11 G4): `signInWithRedirect` is primary now that
 * `authDomain` is the app's own host (the auth handler is served from
 * `/__/auth/*` on this origin by `web/scripts/auth-helpers.mjs`, so its stored
 * state is first-party and survives third-party storage blocking). The
 * `getRedirectResult` effect finishes a redirect on return. If the redirect
 * fails with "missing initial state", the user lands on `?auth_retry=popup`
 * and the Google button uses the popup, once (`lib/googleSignIn.ts`). COOP is
 * same-origin-allow-popups in staticwebapp.config.json so the popup keeps
 * window.opener.
 *
 * Return to the linked page (PRP-MCP-11 G3): the target comes from `?next=` or
 * the `aiea_return` cookie (`lib/postLoginReturn.ts`), the dashboard otherwise.
 * Errors are one plain sentence (`lib/authErrors.ts`), never Firebase's text.
 */

import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../lib/firebase';
import { authErrorMessage, logAuthError } from '../../lib/authErrors';
import {
  POPUP_RETRY_PROMPT,
  completeRedirectSignIn,
  isPopupRetry,
  startGoogleSignIn,
  withPopupRetry,
} from '../../lib/googleSignIn';
import { stashPack } from '../../lib/pendingCheckout';
import { nextFromSearch, readReturnPath, stashReturnPath } from '../../lib/postLoginReturn';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AuthShell } from '../components/AuthShell';

/** Public sign-in screen, V2 dark styling. */
export default function V2LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const popupRetry = isPopupRetry(location.search);

  // Finish a Google redirect that returned here. On success App.tsx's
  // onAuthStateChanged sets the user and the /login route navigates to the
  // return path. Missing initial state -> retry once with the popup.
  useEffect(() => {
    let cancelled = false;
    void completeRedirectSignIn(auth, location.search).then((outcome) => {
      if (cancelled) return;
      if (outcome.kind === 'retry-popup') {
        navigate({ pathname: '/login', search: withPopupRetry(location.search) }, { replace: true });
      } else if (outcome.kind === 'error') {
        setError(outcome.message);
      }
    });
    return () => {
      cancelled = true;
    };
    // Runs once per page load: a redirect result is consumed by the first call.
  }, []);

  // Stash a marketing-link purchase intent (e.g. arriving from the signup
  // page's verification flow with ?pack= still in the URL).
  useEffect(() => {
    const pack = searchParams.get('pack');
    if (pack) stashPack(pack);
  }, [searchParams]);

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }
    setBusy(true);
    setError(null);
    // Read the target BEFORE signing in: once the user is set, ProtectedRoute
    // clears the stored path on landing.
    const target = readReturnPath(location.search) ?? '/';
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // The linked page (or an MCP consent request) takes precedence over the dashboard.
      navigate(target, { replace: true });
    } catch (err) {
      logAuthError(err, 'email sign-in');
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setBusy(true);
    setError(null);
    // Keep the linked page in the cookie as well: the handler's popup fallback
    // comes back to /login without ?next=.
    const next = nextFromSearch(location.search);
    if (next) stashReturnPath(next);
    try {
      // Redirect: this page navigates to /__/auth/handler and back to this URL.
      // Retry page: popup; onAuthStateChanged in App.tsx routes us away from /login.
      await startGoogleSignIn(auth, { popup: popupRetry });
    } catch (err) {
      logAuthError(err, popupRetry ? 'google popup' : 'google redirect');
      setError(authErrorMessage(err));
      setBusy(false);
    }
  };

  return (
    <AuthShell>
      <div className="mb-5">
        <h1 className="text-[19px] font-semibold text-[var(--tx-hi)]">Welcome back</h1>
        <p className="mt-1.5 text-[13px] text-[var(--tx-mid)]">
          Sign in to access your projects and conversions.
        </p>
      </div>

      {error && (
        <p role="alert" className="mb-3 text-[12.5px] font-medium text-[var(--bad)]">
          {error}
        </p>
      )}
      {popupRetry && !error && (
        <p className="mb-3 text-[12.5px] font-medium text-[var(--tx-hi)]">{POPUP_RETRY_PROMPT}</p>
      )}

      {/* Google first — matches the signup screen. */}
      <Button
        variant="primary"
        className="w-full justify-center"
        onClick={handleGoogleSignIn}
        disabled={busy}
        aria-label="Sign in with Google"
      >
        Continue with Google
      </Button>

      <div className="my-4 flex items-center gap-3 text-[11px] text-[var(--tx-faint)]">
        <span className="h-px flex-1 bg-[var(--bd-subtle)]" />
        or sign in with email
        <span className="h-px flex-1 bg-[var(--bd-subtle)]" />
      </div>

      <form onSubmit={handleEmailSignIn} className="grid gap-4">
        <div className="grid gap-1.5">
          <label htmlFor="login-email" className="text-[12px] font-medium text-[var(--tx-mid)]">
            Email
          </label>
          <Input
            id="login-email"
            type="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            aria-label="Email Address"
          />
        </div>
        <div className="grid gap-1.5">
          <label htmlFor="login-password" className="text-[12px] font-medium text-[var(--tx-mid)]">
            Password
          </label>
          <Input
            id="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            aria-label="Password"
          />
        </div>
        <Button type="submit" variant="secondary" className="w-full justify-center" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>

      <p className="mt-4 text-center text-[13px] text-[var(--tx-mid)]">
        Don&apos;t have an account?{' '}
        <Link
          to={{ pathname: '/signup', search: location.search }}
          className="text-[var(--accent-ink)] underline"
        >
          Start free — first 3 conversions on us
        </Link>
      </p>
    </AuthShell>
  );
}
